import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Leaf, ArrowLeft, Download, Users, TrendingDown, TrendingUp, Search, ShieldAlert, AlertTriangle, MessageSquareHeart } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { toast } from "sonner";

const KIND_LABELS = {
  phq9: "PHQ-9",
  gad7: "GAD-7",
  ghq28: "GHQ-28",
  bhs: "BHS",
  swls: "SWLS",
  perma: "PERMA",
};

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [onlyRisk, setOnlyRisk] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/admin/is-admin").then(r => {
      setIsAdmin(!!r.data.is_admin);
      setChecking(false);
      if (r.data.is_admin) loadAll();
    }).catch(() => { setChecking(false); setIsAdmin(false); });
  }, []);

  const loadAll = async () => {
    try {
      const [u, s] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/stats"),
      ]);
      setUsers(u.data);
      setStats(s.data);
    } catch {
      toast.error("No pudimos cargar los datos.");
    }
  };

  const openDetail = async (userId) => {
    try {
      const r = await api.get(`/admin/users/${userId}/detail`);
      setSelected(r.data);
    } catch {
      toast.error("No pudimos cargar el detalle del usuario.");
    }
  };

  const downloadCsv = () => {
    const link = document.createElement("a");
    link.href = `${API}/admin/export.csv`;
    link.setAttribute("download", "healthapp-users.csv");
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  if (checking) {
    return <div className="min-h-screen grid place-items-center bg-[var(--ha-bg)] text-[var(--ha-muted)]">Comprobando permisos…</div>;
  }
  if (!isAdmin) {
    return (
      <main className="min-h-screen grid place-items-center bg-[var(--ha-bg)] px-6" data-testid="admin-forbidden">
        <div className="text-center max-w-md">
          <p className="text-xs uppercase tracking-[0.2em] text-[var(--ha-muted)] font-semibold">Acceso restringido</p>
          <h1 className="mt-3 text-3xl font-extrabold text-[var(--ha-text)]">Solo administradores</h1>
          <p className="mt-3 text-[var(--ha-muted)]">Esta zona está reservada. Inicia sesión con la cuenta de administrador.</p>
          <Button onClick={() => navigate("/dashboard")} className="mt-6 rounded-full bg-[var(--ha-primary)] hover:bg-[var(--ha-primary-hover)] text-white h-11 px-6">
            <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} /> Volver al panel
          </Button>
        </div>
      </main>
    );
  }

  const filtered = users.filter(u => {
    const s = q.trim().toLowerCase();
    const matchesText = !s || (u.email || "").toLowerCase().includes(s) || (u.name || "").toLowerCase().includes(s);
    const matchesRisk = !onlyRisk || (u.risk_flags && u.risk_flags.length > 0);
    return matchesText && matchesRisk;
  });

  const atRiskCount = users.filter(u => u.risk_flags && u.risk_flags.length > 0).length;
  const criticalCount = users.filter(u => (u.risk_flags || []).some(f => f.severity === "critical")).length;

  return (
    <main className="min-h-screen bg-[var(--ha-bg)]" data-testid="admin-page">
      <header className="max-w-6xl mx-auto flex items-center justify-between px-6 py-6">
        <div className="flex items-center gap-2">
          <div className="h-10 w-10 rounded-full bg-[var(--ha-primary)] grid place-items-center shadow-sm">
            <Leaf className="w-5 h-5 text-white" strokeWidth={1.5} />
          </div>
          <span className="font-extrabold tracking-tight text-[var(--ha-text)] text-lg">MINDHOME HEALTH APP · Admin</span>
        </div>
        <Button variant="outline" onClick={() => navigate("/dashboard")} className="rounded-full border-[var(--ha-border)] h-9 px-4" data-testid="admin-back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} /> Volver al panel
        </Button>
      </header>

      <section className="max-w-6xl mx-auto px-6 py-6">
        <p className="text-xs uppercase tracking-[0.2em] text-[var(--ha-muted)] font-semibold">Panel de administración</p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight text-[var(--ha-text)]">Usuarios y resultados</h1>

        {/* Risk banner */}
        {atRiskCount > 0 && (
          <div
            data-testid="risk-banner"
            className={`mt-6 rounded-2xl border p-5 flex items-start gap-3 ${criticalCount > 0 ? "border-[var(--ha-error)]/40 bg-[var(--ha-error)]/10" : "border-orange-400/40 bg-orange-50"}`}
          >
            <ShieldAlert className={`w-5 h-5 mt-0.5 shrink-0 ${criticalCount > 0 ? "text-[var(--ha-error)]" : "text-orange-500"}`} strokeWidth={1.75} />
            <div className="flex-1">
              <p className="font-bold text-[var(--ha-text)]">
                {atRiskCount} usuario{atRiskCount === 1 ? "" : "s"} con alerta clínica
                {criticalCount > 0 && ` · ${criticalCount} crítico${criticalCount === 1 ? "" : "s"} (ideación suicida)`}
              </p>
              <p className="mt-1 text-sm text-[var(--ha-muted)]">Umbrales: PHQ-9 ≥ 20 (depresión severa), GAD-7 ≥ 15 (ansiedad severa), BHS ≥ 14 (desesperanza severa), PHQ-9 Q9 &gt; 0 (ideación suicida).</p>
            </div>
            <Button
              data-testid="risk-filter-btn"
              variant="outline"
              size="sm"
              onClick={() => setOnlyRisk(!onlyRisk)}
              className="rounded-full border-[var(--ha-border)] h-9 px-4"
            >
              {onlyRisk ? "Ver todos" : "Ver solo en riesgo"}
            </Button>
          </div>
        )}

        {/* Stats */}
        {stats && (
          <div className="mt-8 grid md:grid-cols-3 gap-4">
            <Card className="rounded-2xl border-[var(--ha-border)] bg-white">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 text-[var(--ha-muted)]"><Users className="w-4 h-4" strokeWidth={1.75} /><span className="text-xs uppercase tracking-[0.14em] font-bold">Usuarios totales</span></div>
                <p className="mt-2 text-4xl font-extrabold text-[var(--ha-text)]" data-testid="stat-users-total">{stats.users_total}</p>
                {stats.app_feedback && stats.app_feedback.n > 0 && (
                  <div className="mt-4 pt-4 border-t border-[var(--ha-border)] space-y-1.5 text-sm">
                    <p className="text-xs uppercase tracking-[0.12em] text-[var(--ha-muted)] font-bold">Valoración media (n={stats.app_feedback.n})</p>
                    <p className="text-[var(--ha-text)]"><span className="font-semibold">Satisfacción:</span> <span className="text-[var(--ha-primary)] font-bold" data-testid="stat-satisfaction-avg">{stats.app_feedback.satisfaction_avg ?? "—"}</span>/10</p>
                    <p className="text-[var(--ha-text)]"><span className="font-semibold">Ayuda percibida:</span> <span className="text-[var(--ha-primary)] font-bold" data-testid="stat-helpfulness-avg">{stats.app_feedback.helpfulness_avg ?? "—"}</span>/10</p>
                  </div>
                )}
              </CardContent>
            </Card>
            <Card className="rounded-2xl border-[var(--ha-border)] bg-white md:col-span-2">
              <CardContent className="p-6">
                <p className="text-xs uppercase tracking-[0.14em] text-[var(--ha-muted)] font-bold">Completados por sesión</p>
                <div className="mt-3 h-40" style={{ minHeight: "160px" }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={stats.sessions_completion}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--ha-border)" />
                      <XAxis dataKey="session" stroke="var(--ha-muted)" fontSize={11} />
                      <YAxis stroke="var(--ha-muted)" fontSize={11} allowDecimals={false} />
                      <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid var(--ha-border)" }} />
                      <Bar dataKey="users" fill="var(--ha-primary)" radius={[8,8,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tendencias baseline vs final */}
        {stats && stats.questionnaire_means?.length > 0 && (
          <Card className="mt-6 rounded-2xl border-[var(--ha-border)] bg-white">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-3">
                <TrendingDown className="w-4 h-4 text-[var(--ha-accent)]" strokeWidth={1.75} />
                <p className="text-xs uppercase tracking-[0.14em] text-[var(--ha-muted)] font-bold">Tendencia media · Inicial vs Final (todos los usuarios)</p>
              </div>
              <div className="h-64" style={{ minHeight: "280px" }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={stats.questionnaire_means.map(m => ({ name: KIND_LABELS[m.kind] || m.kind, Inicial: m.baseline_avg, Final: m.final_avg }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--ha-border)" />
                    <XAxis dataKey="name" stroke="var(--ha-muted)" fontSize={11} />
                    <YAxis stroke="var(--ha-muted)" fontSize={11} />
                    <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid var(--ha-border)" }} />
                    <Legend />
                    <Bar dataKey="Inicial" fill="var(--ha-accent)" radius={[8,8,0,0]} />
                    <Bar dataKey="Final" fill="var(--ha-primary)" radius={[8,8,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Feedback cualitativo */}
        {stats && stats.app_feedback?.qualitative_feedback?.length > 0 && (
          <Card className="mt-6 rounded-2xl border-[var(--ha-border)] bg-white" data-testid="admin-qualitative-feedback-section">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <MessageSquareHeart className="w-4 h-4 text-[var(--ha-accent)]" strokeWidth={1.75} />
                <p className="text-xs uppercase tracking-[0.14em] text-[var(--ha-muted)] font-bold">
                  Sugerencias abiertas de los pacientes ({stats.app_feedback.qualitative_feedback.length})
                </p>
              </div>
              <ul className="space-y-3 max-h-80 overflow-y-auto pr-2">
                {stats.app_feedback.qualitative_feedback.map((f, i) => (
                  <li
                    key={`${f.user_id}-${i}`}
                    className="rounded-xl border border-[var(--ha-border)] p-4 bg-[var(--ha-bg)]/60"
                    data-testid={`admin-qualitative-feedback-item-${i}`}
                  >
                    <div className="flex items-center justify-between mb-1.5 text-xs">
                      <span className="font-semibold text-[var(--ha-text)]">{f.name || f.email || "Anónimo"}</span>
                      <span className="text-[var(--ha-muted)]">{(f.updated_at || "").slice(0, 10)}</span>
                    </div>
                    <p className="text-sm text-[var(--ha-text)] whitespace-pre-wrap leading-relaxed">{f.text}</p>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Users table */}
        <div className="mt-8 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 text-[var(--ha-muted)] absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              data-testid="admin-search"
              placeholder="Buscar por nombre o email"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9 h-10 rounded-full border-[var(--ha-border)]"
            />
          </div>
          <Button data-testid="admin-download-csv" onClick={downloadCsv} className="rounded-full bg-[var(--ha-primary)] hover:bg-[var(--ha-primary-hover)] text-white h-10 px-5">
            <Download className="w-4 h-4 mr-2" strokeWidth={2} /> Descargar CSV
          </Button>
        </div>

        <Card className="mt-4 rounded-2xl border-[var(--ha-border)] bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Usuario</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Registro</TableHead>
                  <TableHead>Cuestionarios</TableHead>
                  <TableHead>Sesiones completadas</TableHead>
                  <TableHead>Valoración</TableHead>
                  <TableHead>Recordatorios</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(u => {
                  const risks = u.risk_flags || [];
                  const isCritical = risks.some(r => r.severity === "critical");
                  const hasRisk = risks.length > 0;
                  return (
                  <TableRow
                    key={u.user_id}
                    data-testid={`admin-user-row-${u.user_id}`}
                    className={isCritical ? "bg-[var(--ha-error)]/5" : (hasRisk ? "bg-orange-50" : "")}
                  >
                    <TableCell className="font-semibold text-[var(--ha-text)]">
                      <div className="flex items-center gap-2">
                        {hasRisk && (
                          <AlertTriangle
                            data-testid={`risk-icon-${u.user_id}`}
                            className={`w-4 h-4 shrink-0 ${isCritical ? "text-[var(--ha-error)]" : "text-orange-500"}`}
                            strokeWidth={2}
                          />
                        )}
                        <span>{u.name || "—"}</span>
                      </div>
                      {hasRisk && (
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          {risks.map((r, i) => (
                            <span
                              key={i}
                              data-testid={`risk-badge-${u.user_id}-${r.code}`}
                              className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide ${r.severity === "critical" ? "bg-[var(--ha-error)] text-white" : "bg-orange-500 text-white"}`}
                            >
                              {r.label}
                            </span>
                          ))}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-[var(--ha-muted)]">{u.email}</TableCell>
                    <TableCell className="text-[var(--ha-muted)] text-sm">{(u.created_at || "").slice(0, 10)}</TableCell>
                    <TableCell>{u.questionnaires_count}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {u.completed_sessions.length === 0 ? <span className="text-[var(--ha-muted)] text-xs">Ninguna</span> :
                          u.completed_sessions.map(n => (
                            <span key={n} className="inline-block h-6 w-6 rounded-full bg-[var(--ha-primary)]/10 text-[var(--ha-primary)] grid place-items-center text-xs font-bold">{n}</span>
                          ))
                        }
                      </div>
                    </TableCell>
                    <TableCell className="text-xs">
                      {u.app_satisfaction != null ? (
                        <span className="text-[var(--ha-text)] inline-flex items-center gap-1">
                          <span>😊 {u.app_satisfaction} · 💚 {u.app_helpfulness}</span>
                          {(u.app_qualitative_feedback || "").trim().length > 0 && (
                            <MessageSquareHeart
                              className="w-3.5 h-3.5 text-[var(--ha-accent)]"
                              strokeWidth={2}
                              title="Con sugerencia abierta"
                              data-testid={`admin-user-has-qualitative-${u.user_id}`}
                            />
                          )}
                        </span>
                      ) : <span className="text-[var(--ha-muted)]">—</span>}
                    </TableCell>
                    <TableCell className="text-xs text-[var(--ha-muted)]">
                      {u.push_enabled && "📱"} {u.reminders_enabled && "📧"} {!u.push_enabled && !u.reminders_enabled && "—"}
                    </TableCell>
                    <TableCell>
                      <Button
                        data-testid={`admin-view-user-${u.user_id}`}
                        variant="outline"
                        size="sm"
                        onClick={() => openDetail(u.user_id)}
                        className="rounded-full border-[var(--ha-border)] h-8"
                      >
                        Ver detalle
                      </Button>
                    </TableCell>
                  </TableRow>
                  );
                })}
                {filtered.length === 0 && (
                  <TableRow><TableCell colSpan={8} className="text-center text-[var(--ha-muted)] py-8">Sin usuarios.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </Card>
      </section>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="admin-user-detail">
          <DialogHeader>
            <DialogTitle>{selected?.user?.name || "Usuario"}</DialogTitle>
            <DialogDescription>Ficha completa con cuestionarios, objetivos SMART y progreso del usuario.</DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-6">
              <div className="text-sm">
                <p className="text-[var(--ha-muted)]">{selected.user.email}</p>
                <p className="text-[var(--ha-muted)] text-xs">Registrado: {(selected.user.created_at || "").slice(0, 10)}</p>
              </div>

              <div>
                <p className="text-xs uppercase tracking-[0.14em] text-[var(--ha-muted)] font-bold mb-2">Cuestionarios</p>
                <div className="grid sm:grid-cols-2 gap-2">
                  {selected.questionnaires.map((q, i) => (
                    <div key={i} className="rounded-xl border border-[var(--ha-border)] px-3 py-2 text-sm">
                      <p className="font-semibold text-[var(--ha-text)]">{KIND_LABELS[q.kind]} · {q.phase || "baseline"}</p>
                      <p className="text-[var(--ha-muted)]">Puntuación: <span className="font-bold text-[var(--ha-primary)]">{q.score}</span> · {q.interpretation}</p>
                    </div>
                  ))}
                  {selected.questionnaires.length === 0 && <p className="text-sm text-[var(--ha-muted)]">Sin cuestionarios.</p>}
                </div>
              </div>

              <div>
                <p className="text-xs uppercase tracking-[0.14em] text-[var(--ha-muted)] font-bold mb-2">Objetivos SMART</p>
                <ul className="space-y-1 text-sm">
                  {selected.smart_goals.map((g, i) => (
                    <li key={i} className="text-[var(--ha-text)]">• {g.text}</li>
                  ))}
                  {selected.smart_goals.length === 0 && <li className="text-[var(--ha-muted)]">Sin objetivos.</li>}
                </ul>
              </div>

              <div>
                <p className="text-xs uppercase tracking-[0.14em] text-[var(--ha-muted)] font-bold mb-2">Progreso por sesión</p>
                <div className="flex flex-wrap gap-2">
                  {selected.progress
                    .filter(p => p.step !== "_current_step")
                    .map((p, i) => (
                      <span key={i} className="rounded-full border border-[var(--ha-border)] px-3 py-1 text-xs text-[var(--ha-muted)]">
                        S{p.session_number} · {p.step} {p.completed ? "✓" : ""}
                      </span>
                    ))
                  }
                  {selected.progress.filter(p => p.step !== "_current_step").length === 0 && <p className="text-sm text-[var(--ha-muted)]">Sin actividad guardada.</p>}
                </div>
              </div>

              {selected.abc_exercises.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-[0.14em] text-[var(--ha-muted)] font-bold mb-2">Ejercicios ABC · Reformulaciones</p>
                  <ul className="space-y-2 text-sm">
                    {selected.abc_exercises.slice(0, 5).map((r, i) => (
                      <li key={i} className="rounded-xl border border-[var(--ha-border)] p-3">
                        <p className="text-xs text-[var(--ha-muted)]">Escenario {r.scenario_id}</p>
                        <p className="mt-1"><strong>Pensamiento reformulado:</strong> {r.positive_thoughts || "—"}</p>
                        <p><strong>Emoción resultante:</strong> {r.positive_emotions || "—"}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {(() => {
                const satEntry = selected.progress.find(p => p.step === "app-satisfaction");
                const sat = satEntry?.payload || {};
                const hasSat = sat.satisfaction != null || sat.helpfulness != null || (sat.qualitative_feedback || "").trim().length > 0;
                if (!hasSat) return null;
                return (
                  <div data-testid="admin-user-detail-feedback">
                    <p className="text-xs uppercase tracking-[0.14em] text-[var(--ha-muted)] font-bold mb-2">Valoración de MINDHOME HEALTH APP</p>
                    <div className="rounded-xl border border-[var(--ha-border)] p-4 space-y-2 text-sm">
                      <p className="text-[var(--ha-text)]">
                        😊 <span className="font-semibold">Satisfacción:</span> <span className="text-[var(--ha-primary)] font-bold">{sat.satisfaction ?? "—"}</span>/10
                        <span className="mx-3">·</span>
                        💚 <span className="font-semibold">Ayuda percibida:</span> <span className="text-[var(--ha-primary)] font-bold">{sat.helpfulness ?? "—"}</span>/10
                      </p>
                      {(sat.qualitative_feedback || "").trim().length > 0 && (
                        <div className="pt-2 border-t border-[var(--ha-border)]">
                          <p className="text-xs text-[var(--ha-muted)] mb-1">Sugerencia abierta:</p>
                          <p className="text-[var(--ha-text)] whitespace-pre-wrap leading-relaxed" data-testid="admin-user-detail-qualitative">{sat.qualitative_feedback}</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              <div className="text-xs text-[var(--ha-muted)]">
                Preferencias: {JSON.stringify(selected.preferences)}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </main>
  );
}
